import videojs, { VideoJsPlayer } from 'video.js';
import 'videojs-contrib-quality-levels';
//import './index';

import ConcreteButton from './concrete-button';
import ConcreteMenuItem from './concrete-menu-item';

export interface QualityLevel {
	id: string;
	label: string;
	width: number;
	height: number;
	bitrate: number;
	enabled: boolean;
}

export interface QualityLevelList {
	levels_: QualityLevel[];
	selectedIndex_: number;
	length: number;

	[index: number]: QualityLevel;
	addQualityLevel(representation: QualityLevel): QualityLevel;
	on(...args: any[]): void;
}

export interface HlsQualitySelectorPluginOptions {
	displayCurrentQuality?: boolean;
	placementIndex?: number;
	vjsIconClass?: string;
}

const Plugin = videojs.getPlugin('plugin');

/**
 * VideoJS HLS Quality Selector Plugin class.
 */
export class HlsQualitySelectorPlugin extends Plugin {
	private config: HlsQualitySelectorPluginOptions;
	private _qualityButton?: ConcreteButton;

	static get VERSION(): string {
		return '3.0.0';
	}

	/**
	 * Plugin Constructor.
	 *
	 * @param {Player} player - The videojs player instance.
	 * @param {Object} options - The plugin options.
	 */
	constructor(public player: VideoJsPlayer, options: HlsQualitySelectorPluginOptions = {}) {
		super(player);

		this.config = options;

		player.ready(() => {
			player.addClass('vjs-hls-quality-selector');
		});

		// If there is quality levels plugin and the HLS tech exists
		// then continue.
		if (this.player.qualityLevels() && this.getHls()) {
			// Create the quality button.
			this.createQualityButton();
			this.bindPlayerEvents();
		}
	}

	/**
	 * Returns HLS Plugin
	 *
	 * @return {*} - videojs-hls-contrib plugin.
	 */
	getHls(): any {
		return (this.player.tech({ IWillNotUseThisInPlugins: true }) as any).hls;
	}

	/**
	 * Binds listener for quality level changes.
	 */
	bindPlayerEvents(): void {
		this.player.qualityLevels().on('addqualitylevel', this.onAddQualityLevel.bind(this));
	}

	/**
	 * Adds the quality menu button to the player control bar.
	 */
	createQualityButton(): void {
		const { player } = this;
		const { placementIndex = player.controlBar.children().length - 2 } = this.config;

		this._qualityButton = new ConcreteButton(player);

		const concreteButtonInstance = (player.controlBar as any).addChild(
			this._qualityButton,
			{ componentClass: 'qualitySelector' },
			placementIndex
		);

		concreteButtonInstance.addClass('vjs-quality-selector');
		if (!this.config.displayCurrentQuality) {
			const icon = ` ${this.config.vjsIconClass || 'vjs-icon-hd'}`;

			concreteButtonInstance.menuButton_.$('.vjs-icon-placeholder').className += icon;
		} else {
			this.setButtonInnerText('auto');
		}
		concreteButtonInstance.removeClass('vjs-hidden');
	}

	/**
	 *Set inner button text.
	 *
	 * @param {string} text - the text to display in the button.
	 */
	setButtonInnerText(text: string): void {
		this._qualityButton!.menuButton_.$('.vjs-icon-placeholder').innerHTML = text;
	}

	/**
	 * Builds individual quality menu items.
	 *
	 * @param {Object} item - Individual quality menu item.
	 * @return {ConcreteMenuItem} - Menu item
	 */
	getQualityMenuItem(item: { label: string; selected?: boolean; value: any }): ConcreteMenuItem {
		const { player } = this;

		return new ConcreteMenuItem(player, item, this._qualityButton!, this);
	}

	/**
	 * Executed when a quality level is added from HLS playlist.
	 */
	onAddQualityLevel(): void {
		const { player } = this;
		const qualityList = player.qualityLevels();
		const levels = qualityList.levels_ || [];
		const levelItems: any[] = [];

		for (let i = 0; i < levels.length; ++i) {
			if (
				!levelItems.filter((_existingItem) => {
					return _existingItem.item && _existingItem.item.value === levels[i].height;
				}).length
			) {
				const levelItem = this.getQualityMenuItem({
					label: levels[i].height + 'p',
					value: levels[i].height
				});

				levelItems.push(levelItem);
			}
		}

		levelItems.sort((current, next) => {
			if (typeof current !== 'object' || typeof next !== 'object') {
				return -1;
			}
			if (current.item.value < next.item.value) {
				return -1;
			}
			if (current.item.value > next.item.value) {
				return 1;
			}
			return 0;
		});

		levelItems.push(
			this.getQualityMenuItem.call(this, {
				label: player.localize('Auto'),
				value: 'auto',
				selected: true
			})
		);

		if (this._qualityButton) {
			this._qualityButton.createItems = function () {
				return levelItems;
			};
			this._qualityButton.update();
		}
	}

	/**
	 * Sets quality (based on media height)
	 *
	 * @param {number} height - A number representing HLS playlist.
	 */
	setQuality(height: number | 'auto'): void {
		const qualityList = this.player.qualityLevels();

		if (this.config.displayCurrentQuality) {
			this.setButtonInnerText(height === 'auto' ? height : `${height}p`);
		}

		for (let i = 0; i < qualityList.length; ++i) {
			const quality = qualityList[i];

			quality.enabled = quality.height === height || height === 'auto';
		}
		this._qualityButton!.unpressButton();
	}
}

// Register the plugin with video.js.
videojs.registerPlugin('hlsQualitySelector', HlsQualitySelectorPlugin);
